import { APIResponse } from 'types/Request';
import { deleteCookie, getCookie, responseError, responseErrorWithMessage } from 'helpers';
import config from 'config';
import { MatriculaRequest, CancelacionMatricula, SuspensionMatricula, TurnoJura, SetTurnoJura } from 'types/Matriculas';

function revokeCredentials() {
  deleteCookie('accessToken');
  deleteCookie('sessionCookie');
  // eslint-disable-next-line no-restricted-globals
  return location.reload();
}

export async function getSolicitudMatricula(): Promise<MatriculaRequest> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/matriculas`);

    type Response = APIResponse<MatriculaRequest>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las solicitudes.');
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok' || !content.payload) {
      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las solicitudes.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las solicitudes.');
  }
}

export async function postMatriculaRequest(
  data: Partial<MatriculaRequest>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/matriculates/matriculas`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) {
      const body = await res.json().catch(() => null);
      return { ...responseError(), message: body?.message };
    }

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer Post con archivos');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export interface EligibilityResult {
  eligible: boolean;
  reasons: string[];
}

async function getEligibility(path: string): Promise<EligibilityResult> {
  const res = await fetch(`${ config.baseUrl }${ path }`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Authorization: getCookie('accessToken'),
    },
  });

  if (res.status !== 200) {
    throw new Error('No se pudo verificar la elegibilidad.');
  }

  const content = await res.json() as APIResponse<EligibilityResult>;

  if (content.status !== 'ok' || !content.payload) {
    throw new Error('No se pudo verificar la elegibilidad.');
  }

  return content.payload;
}

export async function getCancelacionEligible(): Promise<EligibilityResult> {
  return getEligibility('/matriculates/cancelation/eligible');
}

export async function getSuspensionEligible(): Promise<EligibilityResult> {
  return getEligibility('/matriculates/suspension/eligible');
}

export async function postSolicitudCancelacion(
  data: Partial<CancelacionMatricula>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/matriculates/cancelation`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer Post con archivos');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function postSolicitudSuspension(
  data: Partial<SuspensionMatricula>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/matriculates/suspension`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer Post con archivos');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getTurnosJura(): Promise<TurnoJura[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/matriculas/turnos-jura`);

    type Response = APIResponse<TurnoJura[]>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las solicitudes.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las solicitudes.');
  }
}

export async function postTurnosJura(data: Partial<SetTurnoJura>): Promise<any | undefined> {
  try {
    const res = await fetch(`${ config.baseUrl }/matriculates/matriculas/turnos-jura`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer Post con archivos');
    console.info(data);
    console.info(err);
    throw err;
  }
}

export async function getMatriculaAmount(): Promise<number | null> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/matriculas/payment-amount`);

    type Response = APIResponse<number>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return null;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return null;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las solicitudes.');
    }

    return content.payload ?? null;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las solicitudes.');
  }
}
